/**
 * Builds per-file order PDF upload outcomes from `created_orders`, errors, and parse summary.
 * OCR flags in parse summary are not used as failure signals when a file appears in `created_orders`.
 */

/**
 * @typedef {Object} OrderPdfUploadOutcomeRow
 * @property {string} pdfName
 * @property {boolean} success
 * @property {number|null} [orderId]
 * @property {string|null} [poNumber]
 * @property {string|null} [failureReason]
 * @property {number|null} [fileIndex]
 */

/**
 * Normalizes a filename for loose matching against error strings.
 * @param {string} name
 * @returns {string}
 */
function normalizeFileName(name) {
  return String(name || '')
    .trim()
    .toLowerCase();
}

/**
 * Join key for matching created_orders / parse summary rows.
 * @param {Object|null} row
 * @param {number} fallbackIndex
 * @returns {string}
 */
function fileKeyForRow(row, fallbackIndex = 0) {
  if (row?.pdf_name) {
    return `name:${normalizeFileName(row.pdf_name)}`;
  }
  if (row && row.file_index !== undefined && row.file_index !== null) {
    return `idx:${row.file_index}`;
  }
  return `fallback:${fallbackIndex}`;
}

/**
 * Resolves display name for a file row.
 * @param {Object|null} created
 * @param {Object|null} parse
 * @param {File|null} file
 * @param {number} index
 * @returns {string}
 */
function resolvePdfName(created, parse, file, index) {
  if (created?.pdf_name) return String(created.pdf_name);
  if (parse?.pdf_name) return String(parse.pdf_name);
  if (file?.name) return String(file.name);
  return `File ${index + 1}`;
}

/**
 * PO / corporate ref from created order or parse row.
 * @param {Object|null} created
 * @param {Object|null} parse
 * @returns {string|null}
 */
function resolvePoNumber(created, parse) {
  const fromCreated =
    created?.corporate_order_ref ??
    created?.po_number ??
    created?.order_number ??
    null;
  if (fromCreated != null && fromCreated !== '') return String(fromCreated);
  const preview = parse?.ocr_extraction_preview;
  if (preview?.po_number != null && preview.po_number !== '') {
    return String(preview.po_number);
  }
  return null;
}

/**
 * Maps global errors array entries to filenames when possible.
 * @param {Array} errors
 * @returns {Map<string, string[]>}
 */
function errorsByFileName(errors) {
  const map = new Map();
  const list = Array.isArray(errors) ? errors : [];
  list.forEach(err => {
    const text = typeof err === 'string' ? err : String(err?.message || err || '');
    if (!text) return;
    const prefixMatch = text.match(/^([^:]+):\s*(.+)$/s);
    if (prefixMatch) {
      const name = normalizeFileName(prefixMatch[1]);
      const msg = prefixMatch[2].trim();
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(msg);
    } else {
      const key = '__global__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(text);
    }
  });
  return map;
}

/**
 * Failure reason from parse summary when file was not created.
 * @param {Object|null} parse
 * @param {string[]} fileErrors
 * @param {string[]} globalErrors
 * @returns {string}
 */
function failureReasonFromParse(parse, fileErrors, globalErrors) {
  const parts = [];
  if (fileErrors.length) parts.push(...fileErrors);
  const reviewErrors = parse?.review_errors;
  if (Array.isArray(reviewErrors) && reviewErrors.length) {
    parts.push(...reviewErrors.map(String));
  }
  if (parts.length === 0 && globalErrors.length) {
    parts.push(...globalErrors);
  }
  if (parts.length === 0 && parse?.order_lookup_status) {
    parts.push(`PO lookup: ${parse.order_lookup_status}`);
  }
  if (parts.length === 0) {
    return 'Order was not created for this file.';
  }
  return parts.join('; ');
}

/**
 * Builds one outcome row per uploaded file (and any created_orders without a matching file).
 *
 * @param {Object} params
 * @param {File[]} [params.selectedFiles]
 * @param {Array<Object>} [params.createdOrders]
 * @param {Array<string|Object>} [params.errors]
 * @param {Array<Object>} [params.ocrParseSummary]
 * @returns {OrderPdfUploadOutcomeRow[]}
 */
export function buildOrderPdfUploadOutcomes({
  selectedFiles = [],
  createdOrders = [],
  errors = [],
  ocrParseSummary = [],
}) {
  const files = Array.isArray(selectedFiles) ? selectedFiles : [];
  const created = Array.isArray(createdOrders) ? createdOrders : [];
  const parseRows = Array.isArray(ocrParseSummary) ? ocrParseSummary : [];
  const errMap = errorsByFileName(errors);
  const globalErrors = errMap.get('__global__') || [];

  const createdByKey = new Map();
  created.forEach((row, i) => {
    createdByKey.set(fileKeyForRow(row, i), row);
  });

  const parseByKey = new Map();
  parseRows.forEach((row, i) => {
    parseByKey.set(fileKeyForRow(row, i), row);
  });

  const usedCreatedKeys = new Set();
  const outcomes = [];

  const fileCount = Math.max(files.length, created.length, parseRows.length, 1);

  for (let i = 0; i < fileCount; i += 1) {
    const file = files[i] || null;
    const parse =
      parseByKey.get(fileKeyForRow({ pdf_name: file?.name }, i)) ||
      parseRows[i] ||
      null;
    const key = file
      ? fileKeyForRow({ pdf_name: file.name }, i)
      : fileKeyForRow(parse, i);
    const createdRow =
      createdByKey.get(key) ||
      createdByKey.get(fileKeyForRow(parse, i)) ||
      (created.length === 1 && files.length <= 1 ? created[0] : null);

    const pdfName = resolvePdfName(createdRow, parse, file, i);
    const normName = normalizeFileName(pdfName);
    const fileErrors = errMap.get(normName) || [];

    if (createdRow) {
      usedCreatedKeys.add(fileKeyForRow(createdRow, i));
      outcomes.push({
        pdfName,
        success: true,
        orderId:
          createdRow.order_id != null
            ? Number(createdRow.order_id)
            : createdRow.id != null
              ? Number(createdRow.id)
              : null,
        poNumber: resolvePoNumber(createdRow, parse),
        failureReason: null,
        fileIndex: createdRow.file_index ?? parse?.file_index ?? i,
      });
    } else {
      outcomes.push({
        pdfName,
        success: false,
        orderId: null,
        poNumber: resolvePoNumber(null, parse),
        failureReason: failureReasonFromParse(parse, fileErrors, globalErrors),
        fileIndex: parse?.file_index ?? i,
      });
    }
  }

  created.forEach((row, i) => {
    const key = fileKeyForRow(row, i);
    if (usedCreatedKeys.has(key)) return;
    outcomes.push({
      pdfName: resolvePdfName(row, null, null, i),
      success: true,
      orderId:
        row.order_id != null
          ? Number(row.order_id)
          : row.id != null
            ? Number(row.id)
            : null,
      poNumber: resolvePoNumber(row, null),
      failureReason: null,
      fileIndex: row.file_index ?? null,
    });
  });

  if (outcomes.length === 0 && (globalErrors.length || created.length === 0)) {
    globalErrors.forEach((msg, i) => {
      outcomes.push({
        pdfName: `Upload ${i + 1}`,
        success: false,
        orderId: null,
        poNumber: null,
        failureReason: msg,
        fileIndex: null,
      });
    });
  }

  return outcomes;
}

/**
 * True when error text indicates a duplicate / already-existing order.
 * Kept in sync with `categorizeErrors` duplicate detection in PdfUploadComponent.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isDuplicateOrderErrorMessage(text) {
  const errorLower = String(text || '').toLowerCase();
  return (
    errorLower.includes('duplicate order') ||
    errorLower.includes('order already exists')
  );
}

/**
 * Filters API `errors` before upload toasts. Mirrors outcome-table intent:
 * files in `created_orders` should not surface error toasts.
 *
 * @param {Object} params
 * @param {Array<string|Object>} [params.errors]
 * @param {Array<Object>} [params.createdOrders]
 * @param {File[]} [params.selectedFiles]
 * @returns {string[]}
 */
export function filterErrorsForUploadToasts({
  errors = [],
  createdOrders = [],
  selectedFiles = [],
}) {
  const createdNames = new Set(
    (createdOrders || [])
      .map(o => normalizeFileName(o.pdf_name))
      .filter(Boolean),
  );
  const files = Array.isArray(selectedFiles) ? selectedFiles : [];
  const singleFileName =
    files.length === 1 && files[0]?.name
      ? normalizeFileName(files[0].name)
      : null;
  const hasCreatedOrders = (createdOrders || []).length > 0;
  const list = Array.isArray(errors) ? errors : [];

  return list.filter(err => {
    const text = typeof err === 'string' ? err : String(err?.message || err || '');
    if (!text) return false;

    const prefixMatch = text.match(/^([^:]+):\s*(.+)$/s);
    const fileNorm = prefixMatch ? normalizeFileName(prefixMatch[1]) : null;
    const message = prefixMatch ? prefixMatch[2].trim() : text;

    if (fileNorm && createdNames.has(fileNorm)) {
      return false;
    }

    if (
      files.length <= 1 &&
      hasCreatedOrders &&
      isDuplicateOrderErrorMessage(message) &&
      (!fileNorm || fileNorm === singleFileName)
    ) {
      return false;
    }

    return true;
  });
}

/**
 * Drops review candidates for files that already have an order from upload.
 * @param {Array} candidates
 * @param {Array<Object>} createdOrders
 * @returns {Array}
 */
export function filterReviewCandidatesForCreatedOrders(candidates, createdOrders) {
  const createdNames = new Set(
    (createdOrders || [])
      .map(o => normalizeFileName(o.pdf_name))
      .filter(Boolean)
  );
  return (candidates || []).filter(
    c => !createdNames.has(normalizeFileName(c.pdf_name))
  );
}

/**
 * Updates or inserts a success outcome after reviewed OCR submit.
 * @param {OrderPdfUploadOutcomeRow[]} outcomes
 * @param {string} pdfName
 * @param {number|null} orderId
 * @returns {OrderPdfUploadOutcomeRow[]}
 */
export function markOutcomeReviewSuccess(outcomes, pdfName, orderId) {
  const norm = normalizeFileName(pdfName);
  let found = false;
  const next = (outcomes || []).map(row => {
    if (normalizeFileName(row.pdfName) === norm) {
      found = true;
      return {
        ...row,
        success: true,
        orderId: orderId != null ? Number(orderId) : row.orderId,
        failureReason: null,
      };
    }
    return row;
  });
  if (!found) {
    next.push({
      pdfName: pdfName || 'File',
      success: true,
      orderId: orderId != null ? Number(orderId) : null,
      poNumber: null,
      failureReason: null,
      fileIndex: null,
    });
  }
  return next;
}
